import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Star, MapPin, ChefHat, User, Calendar, Quote } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SharedVisit = () => {
  const { shareCode } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadVisit = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/visits/shared/${shareCode}`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Geteilter Besuch nicht gefunden");
    } finally {
      setLoading(false);
    }
  }, [shareCode]);

  useEffect(() => {
    loadVisit();
  }, [loadVisit]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4" data-testid="shared-visit-error">
        <Star className="w-12 h-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button onClick={() => navigate('/')} className="bg-primary text-primary-foreground">
          Zur Startseite
        </Button>
      </div>
    );
  }

  const { visit, restaurant, user } = data;

  return (
    <div className="min-h-screen bg-background" data-testid="shared-visit-page">
      <header className="border-b border-border bg-card/50">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <Star className="w-5 h-5 text-primary fill-primary" />
            <span className="font-semibold gradient-text">Étoile</span>
          </div>
          <Button size="sm" onClick={() => navigate('/auth')} className="bg-primary text-primary-foreground" data-testid="shared-visit-cta">
            Eigene Besuche tracken
          </Button>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <div className="flex justify-center gap-1 mb-3">
            {[...Array(visit.stars)].map((_, i) => (
              <Star key={`hero-star-${i}`} className="w-8 h-8 text-primary fill-primary" />
            ))}
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-1">{visit.restaurant_name}</h1>
          {restaurant && (
            <p className="text-muted-foreground flex items-center justify-center gap-1">
              <MapPin className="w-4 h-4" />
              {restaurant.location || restaurant.city}, {restaurant.country}
            </p>
          )}
        </div>

        <Card className="bg-card border-border mb-6" data-testid="shared-visit-card">
          <CardContent className="p-6 space-y-4">
            {user && (
              <div className="flex items-center gap-3 pb-4 border-b border-border">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-12 h-12 rounded-full" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                )}
                <div>
                  <p className="font-semibold">{user.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {user.badge_tier} · {user.total_stars} Sterne gesammelt
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-primary" />
              <span>
                Besucht am {visit.visit_date ? new Date(visit.visit_date).toLocaleDateString('de-DE') : "-"}
                {visit.visit_count > 1 && ` · ${visit.visit_count} Besuche`}
              </span>
            </div>

            {restaurant?.cuisine_type && (
              <div className="flex items-center gap-2 text-sm">
                <ChefHat className="w-4 h-4 text-primary" />
                <span>{restaurant.cuisine_type}</span>
              </div>
            )}

            {visit.notes && (
              <div className="flex gap-2 p-3 bg-muted/20 rounded-lg">
                <Quote className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <p className="text-sm italic">{visit.notes}</p>
              </div>
            )}

            {visit.photos?.length > 0 && (
              <div className="grid grid-cols-2 gap-2" data-testid="shared-visit-photos">
                {visit.photos.map((photoId) => (
                  <img
                    key={photoId}
                    src={`${API}/api/upload/photo/${photoId}`}
                    alt="Besuchsfoto"
                    className="w-full h-40 object-cover rounded-lg border border-border"
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-6 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Étoile ist die Plattform für Feinschmecker: Tracke deine Besuche in Michelin-Sterne-Restaurants,
              sammle Sterne und Abzeichen und teile deine kulinarische Reise.
            </p>
            <Button onClick={() => navigate('/auth')} className="bg-primary text-primary-foreground" data-testid="shared-visit-cta-bottom">
              Jetzt kostenlos starten
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SharedVisit;
